import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Heart, Users, Award, Star, Trophy, TrendingUp, Gift,
  MessageCircle, Send, Sparkles, Crown, Zap, GraduationCap,
  Sun, Lightbulb, ThumbsUp, PartyPopper
} from "lucide-react";

const CATEGORY_ICONS = {
  teamwork: Users,
  compassion: Heart,
  excellence: Award,
  innovation: Lightbulb,
  mentorship: GraduationCap,
  positivity: Sun,
  dedication: Star,
  communication: MessageCircle
};

const CATEGORY_COLORS = {
  teamwork: "bg-blue-100 text-blue-700 border-blue-200",
  compassion: "bg-red-100 text-red-700 border-red-200",
  excellence: "bg-yellow-100 text-yellow-700 border-yellow-200",
  innovation: "bg-amber-100 text-amber-700 border-amber-200",
  mentorship: "bg-purple-100 text-purple-700 border-purple-200",
  positivity: "bg-orange-100 text-orange-700 border-orange-200",
  dedication: "bg-indigo-100 text-indigo-700 border-indigo-200",
  communication: "bg-green-100 text-green-700 border-green-200"
};

export default function PeerRecognition() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [kudosFeed, setKudosFeed] = useState([]);
  const [myKudos, setMyKudos] = useState({ received: [], given: [] });
  const [myBadges, setMyBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGiveKudos, setShowGiveKudos] = useState(false);
  const [newKudos, setNewKudos] = useState({
    to_user_id: "",
    category_id: "",
    message: "",
    is_public: true
  });
  
  const userId = user?.id || "demo_user";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, feedRes, receivedRes, givenRes, badgesRes, leaderboardRes, statsRes] = await Promise.all([
        api.get("/phase26/kudos/categories"),
        api.get("/phase26/kudos/feed?limit=30"),
        api.get(`/phase26/kudos/user/${userId}?type=received`),
        api.get(`/phase26/kudos/user/${userId}?type=given`),
        api.get(`/phase26/kudos/badges/${userId}`),
        api.get("/phase26/kudos/leaderboard?timeframe=month"),
        api.get("/phase26/kudos/stats")
      ]);
      
      setCategories(categoriesRes.data.categories || []);
      setKudosFeed(feedRes.data.kudos || []);
      setMyKudos({
        received: receivedRes.data.kudos || [],
        given: givenRes.data.kudos || []
      });
      setMyBadges(badgesRes.data.badges || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load recognition data");
    } finally {
      setLoading(false);
    }
  };

  const submitKudos = async () => {
    if (!newKudos.to_user_id || !newKudos.category_id || !newKudos.message) {
      toast.error("Please fill in all fields");
      return;
    }
    
    try {
      const res = await api.post(`/phase26/kudos?from_user_id=${userId}`, newKudos);
      toast.success("Kudos sent! 🎉");
      
      if (res.data.new_badges_earned?.length > 0) {
        toast.success(`${res.data.new_badges_earned[0].name} badge earned!`);
      }
      
      setShowGiveKudos(false);
      setNewKudos({ to_user_id: "", category_id: "", message: "", is_public: true });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send kudos");
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="recognition-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white" data-testid="peer-recognition">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-8 px-4">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Sparkles className="h-8 w-8" />
                Peer Recognition
              </h1>
              <p className="text-amber-100">Celebrate your amazing colleagues</p>
            </div>
            <Dialog open={showGiveKudos} onOpenChange={setShowGiveKudos}>
              <DialogTrigger asChild>
                <Button className="bg-white text-amber-600 hover:bg-amber-50">
                  <Gift className="w-4 h-4 mr-2" />
                  Give Kudos
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <PartyPopper className="w-5 h-5 text-amber-500" />
                    Give Kudos
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Who are you recognizing?</label>
                    <Input
                      placeholder="Enter colleague's name or ID"
                      value={newKudos.to_user_id}
                      onChange={(e) => setNewKudos(prev => ({...prev, to_user_id: e.target.value}))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Recognition Category</label>
                    <Select
                      value={newKudos.category_id}
                      onValueChange={(v) => setNewKudos(prev => ({...prev, category_id: v}))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => {
                          const Icon = CATEGORY_ICONS[cat.id] || Star;
                          return (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                {cat.name}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Your Message</label>
                    <Textarea
                      placeholder="Tell everyone why this person deserves recognition..."
                      value={newKudos.message}
                      onChange={(e) => setNewKudos(prev => ({...prev, message: e.target.value}))}
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowGiveKudos(false)}>Cancel</Button>
                  <Button onClick={submitKudos} className="bg-amber-500 hover:bg-amber-600">
                    <Send className="w-4 h-4 mr-2" />
                    Send Kudos
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4 text-center">
              <Gift className="w-8 h-8 mx-auto text-amber-500 mb-2" />
              <p className="text-2xl font-bold">{stats?.total_kudos || 0}</p>
              <p className="text-sm text-muted-foreground">Total Kudos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <TrendingUp className="w-8 h-8 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold">{stats?.kudos_this_month || 0}</p>
              <p className="text-sm text-muted-foreground">This Month</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Heart className="w-8 h-8 mx-auto text-red-500 mb-2" />
              <p className="text-2xl font-bold">{myKudos.received?.length || 0}</p>
              <p className="text-sm text-muted-foreground">Received</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Star className="w-8 h-8 mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold">{myBadges?.length || 0}</p>
              <p className="text-sm text-muted-foreground">Badges Earned</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Feed */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="feed" className="space-y-4">
              <TabsList>
                <TabsTrigger value="feed" data-testid="feed-tab">
                  <Sparkles className="w-4 h-4 mr-2" />Feed
                </TabsTrigger>
                <TabsTrigger value="received" data-testid="received-tab">
                  <Heart className="w-4 h-4 mr-2" />Received
                </TabsTrigger>
                <TabsTrigger value="given" data-testid="given-tab">
                  <Gift className="w-4 h-4 mr-2" />Given
                </TabsTrigger>
              </TabsList>

              <TabsContent value="feed">
                <Card>
                  <CardHeader>
                    <CardTitle>Recognition Feed</CardTitle>
                    <CardDescription>See all the amazing work being recognized</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[600px]">
                      <div className="space-y-4">
                        {kudosFeed.map((kudos) => (
                          <KudosCard key={kudos.id} kudos={kudos} formatTime={formatTime} />
                        ))}
                        {kudosFeed.length === 0 && (
                          <div className="text-center py-12">
                            <Gift className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                            <p className="text-muted-foreground">No kudos yet. Be the first to recognize someone!</p>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="received">
                <Card>
                  <CardHeader>
                    <CardTitle>Kudos You've Received</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {myKudos.received.map((kudos) => (
                        <KudosCard key={kudos.id} kudos={kudos} formatTime={formatTime} />
                      ))}
                      {myKudos.received.length === 0 && (
                        <p className="text-center py-8 text-muted-foreground">No kudos received yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="given">
                <Card>
                  <CardHeader>
                    <CardTitle>Kudos You've Given</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {myKudos.given.map((kudos) => (
                        <KudosCard key={kudos.id} kudos={kudos} formatTime={formatTime} />
                      ))}
                      {myKudos.given.length === 0 && (
                        <p className="text-center py-8 text-muted-foreground">
                          You haven't given any kudos yet. Start recognizing your colleagues!
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Leaderboard */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Monthly Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {leaderboard.map((entry) => (
                    <div
                      key={entry.user_id}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        entry.rank === 1 ? "bg-yellow-50 border border-yellow-200" :
                        entry.rank === 2 ? "bg-gray-50 border border-gray-200" :
                        entry.rank === 3 ? "bg-orange-50 border border-orange-200" :
                        ""
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          entry.rank === 1 ? "bg-yellow-200 text-yellow-800" :
                          entry.rank === 2 ? "bg-gray-200 text-gray-800" :
                          entry.rank === 3 ? "bg-orange-200 text-orange-800" :
                          "bg-slate-100 text-slate-600"
                        }`}>
                          {entry.rank}
                        </div>
                        <span className="font-medium">{entry.name}</span>
                      </div>
                      <Badge variant="outline">{entry.kudos_count} kudos</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* My Badges */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-500" />
                  My Badges
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myBadges.length === 0 ? (
                  <p className="text-center py-4 text-muted-foreground text-sm">
                    Give and receive kudos to earn badges!
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {myBadges.map((badge) => (
                      <div
                        key={badge.id}
                        className="p-2 rounded-lg bg-purple-50 border border-purple-200 text-center"
                        title={badge.description}
                      >
                        <div className="w-8 h-8 mx-auto mb-1 rounded-full bg-purple-100 flex items-center justify-center">
                          {badge.icon === "star" ? <Star className="w-4 h-4 text-purple-600" /> :
                           badge.icon === "crown" ? <Crown className="w-4 h-4 text-purple-600" /> :
                           badge.icon === "zap" ? <Zap className="w-4 h-4 text-purple-600" /> :
                           <Award className="w-4 h-4 text-purple-600" />}
                        </div>
                        <p className="text-xs font-medium truncate">{badge.name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Categories */}
            <Card>
              <CardHeader>
                <CardTitle>Recognition Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {categories.map((cat) => {
                    const Icon = CATEGORY_ICONS[cat.id] || Star;
                    return (
                      <div
                        key={cat.id}
                        className={`flex items-center gap-2 p-2 rounded-lg ${CATEGORY_COLORS[cat.id]}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{cat.name}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

// Kudos Card Component
function KudosCard({ kudos, formatTime }) {
  const Icon = CATEGORY_ICONS[kudos.category_id] || Star;
  
  return (
    <div className={`p-4 rounded-lg border ${CATEGORY_COLORS[kudos.category_id] || "bg-slate-50"}`}>
      <div className="flex items-start gap-3">
        <Avatar>
          <AvatarFallback>{kudos.from_user_name?.charAt(0) || "?"}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold">{kudos.from_user_name}</span>
            <span className="text-muted-foreground">gave kudos to</span>
            <span className="font-semibold">{kudos.to_user_name}</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs">
              <Icon className="w-3 h-3 mr-1" />
              {kudos.category_name}
            </Badge>
            <span className="text-xs text-muted-foreground">{formatTime(kudos.created_at)}</span>
          </div>
          <p className="text-sm">{kudos.message}</p>
          {kudos.reactions?.length > 0 && (
            <div className="flex gap-1 mt-2">
              {kudos.reactions.map((r, i) => (
                <span key={i} className="text-sm">{r.emoji}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
